// Object tool — Face / Skin / Hair detection and masked adjustment, 100% in the
// browser. One BiSeNet face-parsing pass gives a 19-class label map; the three
// selectable "objects" are just different unions of those classes, so switching
// between Face, Skin and Hair costs nothing after the first detection.
//
// Nothing is uploaded: the model is fetched once from a CDN into Cache Storage
// and every pixel operation happens on local canvases.

// CelebAMask-HQ class ids produced by the parser.
// 0 background, 1 skin, 2 l_brow, 3 r_brow, 4 l_eye, 5 r_eye, 6 eye_g, 7 l_ear,
// 8 r_ear, 9 ear_r, 10 nose, 11 mouth, 12 u_lip, 13 l_lip, 14 neck, 15 neck_l,
// 16 cloth, 17 hair, 18 hat
const CLS_FACE = [1, 2, 3, 4, 5, 6, 10, 11, 12, 13];
const CLS_SKIN = [...CLS_FACE, 7, 8, 14];
const CLS_HAIR = [17];
// Shadow lift deliberately covers bare skin only — skin, nose, ears, neck. The
// lift targets dark pixels, so including hair, brows, eyes, glasses or clothing
// would wash those out (a black suit turning grey) instead of opening up the
// shadow under the chin, which is the whole point.
const CLS_SHADOW = [1, 7, 8, 10, 14];

export const OBJECT_KINDS = ['face', 'skin', 'hair', 'shadow'];

const KIND_CLASSES = { face: CLS_FACE, skin: CLS_SKIN, hair: CLS_HAIR, shadow: CLS_SHADOW };

const PARSE_SIZE = 512;
// Mean/std the parser was trained with (ImageNet).
const MEAN = [0.485, 0.456, 0.406];
const STD = [0.229, 0.224, 0.225];

export const DEFAULT_OBJECT_SETTINGS = {
    face: { brightness: 100, contrast: 100 },
    skin: { brightness: 100, contrast: 100 },
    hair: { darken: 0 },
    shadow: { lift: 0 },
};

export function cloneObjectSettings(s = DEFAULT_OBJECT_SETTINGS) {
    return {
        face: { ...s.face },
        skin: { ...s.skin },
        hair: { ...s.hair },
        shadow: { ...s.shadow },
    };
}

export function kindHasChange(settings, kind) {
    if (kind === 'hair') return settings.hair.darken !== 0;
    if (kind === 'shadow') return settings.shadow.lift !== 0;
    return settings[kind].brightness !== 100 || settings[kind].contrast !== 100;
}

export function hasObjectChange(settings) {
    return OBJECT_KINDS.some((k) => kindHasChange(settings, k));
}

export function loadImageEl(src) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        // blob:/data: URLs are same-origin already; asking for CORS on them is
        // pointless, and on a remote URL without CORS headers it turns a
        // readable image into a load failure.
        if (!/^(blob:|data:)/i.test(String(src))) img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('IMAGE_LOAD: could not decode the photo'));
        img.src = src;
    });
}

// Letterbox the photo into the parser's fixed 512×512 input so faces keep their
// aspect ratio (squashing a 3:4 passport crop measurably hurts the hair edge).
function buildParserInput(img) {
    const iw = img.naturalWidth, ih = img.naturalHeight;
    const scale = Math.min(PARSE_SIZE / iw, PARSE_SIZE / ih);
    const sw = Math.round(iw * scale), sh = Math.round(ih * scale);
    const dx = Math.floor((PARSE_SIZE - sw) / 2), dy = Math.floor((PARSE_SIZE - sh) / 2);

    const c = document.createElement('canvas');
    c.width = PARSE_SIZE; c.height = PARSE_SIZE;
    const ctx = c.getContext('2d', { willReadFrequently: true });
    // Transparent PNGs (background already removed) must not become black —
    // a neutral grey pad keeps the parser's statistics sane.
    ctx.fillStyle = '#808080';
    ctx.fillRect(0, 0, PARSE_SIZE, PARSE_SIZE);
    ctx.drawImage(img, dx, dy, sw, sh);

    let data;
    try {
        ({ data } = ctx.getImageData(0, 0, PARSE_SIZE, PARSE_SIZE));
    } catch (err) {
        throw new Error(`CANVAS_TAINTED: ${err?.message || err}`);
    }
    // Kept alongside the tensor: facial-hair detection needs the parser-space
    // luminance, and re-rasterising the photo later just to get it is wasteful.
    const luma = new Uint8Array(PARSE_SIZE * PARSE_SIZE);
    // Saturation, used only to tell a colour photo from a greyscale one.
    const sat = new Uint8Array(PARSE_SIZE * PARSE_SIZE);
    // Hue, quantised to a byte (0..255 spans the full circle). This is what
    // separates facial hair from merely shadowed skin: shaded skin keeps the
    // person's own orange-red hue, while beard sits far from it. Comparing hue
    // *against this photo's own skin* also survives a colour cast, which a plain
    // saturation threshold does not — a teal-lit beard is more saturated than
    // forehead, so the old test threw away most of the beard.
    const hue = new Uint8Array(PARSE_SIZE * PARSE_SIZE);
    const plane = PARSE_SIZE * PARSE_SIZE;
    const input = new Float32Array(plane * 3);
    for (let p = 0, i = 0; p < plane; p++, i += 4) {
        input[p] = (data[i] / 255 - MEAN[0]) / STD[0];
        input[plane + p] = (data[i + 1] / 255 - MEAN[1]) / STD[1];
        input[plane * 2 + p] = (data[i + 2] / 255 - MEAN[2]) / STD[2];
        const r = data[i], g = data[i + 1], b = data[i + 2];
        luma[p] = (0.299 * r + 0.587 * g + 0.114 * b) | 0;
        const mx = r > g ? (r > b ? r : b) : (g > b ? g : b);
        const mn = r < g ? (r < b ? r : b) : (g < b ? g : b);
        sat[p] = mx === 0 ? 0 : (((mx - mn) / mx) * 255) | 0;
        // Opponent axes → angle. Byte 0..255 wraps the circle, so a circular
        // difference is just a byte subtraction (≈1.4° per step).
        hue[p] = ((Math.atan2(g - b, r - g) + Math.PI) * (255 / (2 * Math.PI))) | 0;
    }
    return { input, luma, sat, hue, place: { dx, dy, sw, sh } };
}

// The worker is kept alive between runs on purpose: tearing it down would drop
// the loaded ONNX session, so every detection would pay the model load again
// (and, in contexts without persistent storage, the whole download).
let parseWorker = null;
const pending = new Map();   // request id → { resolve, reject, onProgress }
let nextRequestId = 1;
let queue = Promise.resolve(); // ONNX sessions are single-run; serialize callers

function getWorker() {
    if (parseWorker) return parseWorker;
    parseWorker = new Worker(new URL('./faceParsingWorker.js', import.meta.url), { type: 'module' });
    parseWorker.onmessage = (e) => {
        const { id, type, pct, stage, labels, message } = e.data;
        const req = pending.get(id);
        if (!req) return;
        if (type === 'progress') req.onProgress?.('download', pct);
        else if (type === 'stage') req.onProgress?.(stage, 0);
        else if (type === 'done') { pending.delete(id); req.resolve(labels); }
        else if (type === 'error') {
            pending.delete(id);
            dropWorker(); // a failed run can leave the session unusable
            req.reject(new Error(message || 'failed'));
        }
    };
    parseWorker.onerror = (err) => {
        const failure = new Error(`WORKER: ${err?.message || 'failed to start'}${err?.filename ? ` @ ${err.filename}:${err.lineno}` : ''}`);
        const waiting = [...pending.values()];
        pending.clear();
        dropWorker();
        waiting.forEach((r) => r.reject(failure));
    };
    return parseWorker;
}

function dropWorker() {
    try { parseWorker?.terminate(); } catch { /* already gone */ }
    parseWorker = null;
}

// ── Facial hair ───────────────────────────────────────────────────────────────
// CelebAMask-HQ (what this parser was trained on) has no beard/moustache class:
// facial hair comes back labelled plain `skin`. So Hair would ignore a beard,
// and Shadow would treat it as shadowed skin and wash it grey. We recover it
// from tone instead — inside the lower face, beard pixels are markedly darker
// than cheek skin — using Otsu's threshold so it adapts to any skin tone.

// Split a histogram into dark/light at the variance-maximising threshold.
function otsuThreshold(hist, total) {
    let sum = 0;
    for (let i = 0; i < 256; i++) sum += i * hist[i];
    let sumB = 0, wB = 0, best = 0, bestVar = -1;
    for (let t = 0; t < 256; t++) {
        wB += hist[t];
        if (!wB) continue;
        const wF = total - wB;
        if (!wF) break;
        sumB += t * hist[t];
        const mB = sumB / wB;
        const mF = (sum - sumB) / wF;
        const between = wB * wF * (mB - mF) * (mB - mF);
        if (between > bestVar) { bestVar = between; best = t; }
    }
    return best;
}

/** Shortest distance between two hue bytes, in byte steps (128 = opposite). */
function hueGap(a, b) {
    const d = Math.abs(a - b);
    return d > 128 ? 256 - d : d;
}

// Beard hue must sit this far from the person's own lit skin. Measured on real
// photos: a full beard lands ~165° away (a shaded jaw only ~12°), so 40° splits
// them with room to spare — it caught 91% of a beard and 0% of a clean-shaven
// jaw in the same lighting.
const BEARD_HUE_GAP = Math.round((40 / 360) * 256);

function detectFacialHair(labels, luma, sat, hue, size) {
    const hair = new Uint8Array(size * size);

    // Geometry: the beard/moustache zone is the face, from the top of the nose
    // down. Sideburns sit a little higher, which the nose top comfortably covers.
    let noseTop = Infinity, noseBot = -1, faceX0 = Infinity, faceX1 = -1, faceY1 = -1;
    const faceIds = new Set(CLS_FACE);
    for (let p = 0, y = 0; y < size; y++) {
        for (let x = 0; x < size; x++, p++) {
            const l = labels[p];
            if (l === 10) { if (y < noseTop) noseTop = y; if (y > noseBot) noseBot = y; }
            if (faceIds.has(l)) {
                if (x < faceX0) faceX0 = x;
                if (x > faceX1) faceX1 = x;
                if (y > faceY1) faceY1 = y;
            }
        }
    }
    if (!isFinite(noseTop) || faceX1 < 0) return hair; // no usable face
    // Start halfway down the nose: the eye sockets sit above that line, so their
    // dark, low-saturation corners can never be mistaken for a moustache.
    const bandTop = Math.round((noseTop + Math.max(noseTop, noseBot)) / 2);

    // Reference tone: bare skin above the nose (forehead, upper cheeks) is never
    // beard, so it gives a clean "what does this person's skin look like" value.
    const refHist = new Uint32Array(256);
    const refSatHist = new Uint32Array(256);
    // Hue is an angle, so it averages as a vector — a plain mean would put the
    // midpoint of 350° and 10° at 180°, the opposite colour.
    let refHueX = 0;
    let refHueY = 0;
    let refCount = 0;
    for (let p = 0, y = 0; y < noseTop; y++) {
        p = y * size;
        for (let x = 0; x < size; x++, p++) {
            if (labels[p] === 1) {
                refHist[luma[p]]++;
                refSatHist[sat[p]]++;
                const a = (hue[p] / 255) * 2 * Math.PI;
                refHueX += Math.cos(a);
                refHueY += Math.sin(a);
                refCount++;
            }
        }
    }
    if (refCount < 200) return hair;
    const median = (hist) => {
        let acc = 0;
        for (let i = 0; i < 256; i++) { acc += hist[i]; if (acc >= refCount / 2) return i; }
        return 128;
    };
    const refMedian = median(refHist);
    const refSat = median(refSatHist);
    const refHue = Math.round(
        ((Math.atan2(refHueY, refHueX) + 2 * Math.PI) % (2 * Math.PI)) * (255 / (2 * Math.PI)),
    );

    // Candidates: skin/neck pixels in the lower face. Lips and mouth carry their
    // own labels and are left out, so they never get darkened as "beard".
    const hist = new Uint32Array(256);
    let count = 0;
    const inBand = (x, y) => y >= bandTop && y <= faceY1 + (faceY1 - bandTop) * 0.6
        && x >= faceX0 && x <= faceX1;
    for (let y = bandTop; y < size; y++) {
        for (let x = 0, p = y * size; x < size; x++, p++) {
            const l = labels[p];
            if ((l === 1 || l === 14) && inBand(x, y)) { hist[luma[p]]++; count++; }
        }
    }
    if (count < 400) return hair;

    const t = otsuThreshold(hist, count);
    let darkSum = 0, darkN = 0, lightSum = 0, lightN = 0;
    for (let i = 0; i < 256; i++) {
        if (i <= t) { darkSum += i * hist[i]; darkN += hist[i]; }
        else { lightSum += i * hist[i]; lightN += hist[i]; }
    }
    if (!darkN || !lightN) return hair;
    const darkMean = darkSum / darkN;
    const lightMean = lightSum / lightN;
    const darkShare = darkN / count;

    // Guards against "detecting" a beard on a clean-shaven face: the two clusters
    // must be genuinely far apart, the dark one clearly below reference skin, and
    // its size plausible for facial hair rather than half the face in shade.
    if (darkMean > lightMean * 0.72) return hair;
    if (darkMean > refMedian * 0.8) return hair;
    if (darkShare < 0.03 || darkShare > 0.6) return hair;

    // Otsu alone is too generous on a low-key photo: with dim lighting its split
    // lands high enough to swallow shaded cheeks and jaw. Facial hair is also
    // *absolutely* much darker than the person's own skin, so cap the cut there.
    const cut = Math.min(t, refMedian * 0.7);
    // Second gate: a shadowed cheek is dark but still *skin-coloured*, so the
    // pixel's hue must sit well away from this person's own lit skin. Skipped on
    // greyscale/near-greyscale photos, where hue says nothing.
    const useHue = refSat >= 25;
    const candidate = (x, y, p) => (labels[p] === 1 || labels[p] === 14) && inBand(x, y);
    for (let y = bandTop; y < size; y++) {
        for (let x = 0, p = y * size; x < size; x++, p++) {
            if (!candidate(x, y, p) || luma[p] > cut) continue;
            if (useHue && hueGap(hue[p], refHue) < BEARD_HUE_GAP) continue;
            hair[p] = 1;
        }
    }

    // The two gates only keep the darkest core of the beard, which leaves it
    // patchy. Grow it one ring — but only into pixels that were candidates in the
    // first place, so it can spread through the beard without touching lips,
    // nostrils or anything outside the band.
    const grown = hair.slice();
    for (let y = bandTop + 1; y < size - 1; y++) {
        for (let x = 1, p = y * size + 1; x < size - 1; x++, p++) {
            if (hair[p] || !candidate(x, y, p)) continue;
            if (hair[p - 1] || hair[p + 1] || hair[p - size] || hair[p + size]) grown[p] = 1;
        }
    }
    return grown;
}

function runWorker(input, onProgress) {
    const start = () => new Promise((resolve, reject) => {
        const id = nextRequestId++;
        pending.set(id, { resolve, reject, onProgress });
        getWorker().postMessage({ id, input }, [input.buffer]);
    });
    // Chain onto the previous run (whether it succeeded or not) so only one
    // inference is ever in flight on the shared session.
    const run = queue.then(start, start);
    queue = run.catch(() => {});
    return run;
}

/**
 * Parse a photo into Face / Skin / Hair regions, entirely client-side.
 *
 * @param {string} imageSrc object URL / data URL of the photo
 * @param {(stage:'download'|'detect', pct:number)=>void} [onProgress]
 * @returns {Promise<{image:HTMLImageElement, labels:Uint8Array, place:object,
 *                    imgW:number, imgH:number, found:Record<string,boolean>,
 *                    maskCache:Map}>}
 */
export async function runObjectDetect(imageSrc, onProgress) {
    const image = await loadImageEl(imageSrc);
    const { input, luma, sat, hue, place } = buildParserInput(image);
    const labels = await runWorker(input, onProgress);
    const facialHair = detectFacialHair(labels, luma, sat, hue, PARSE_SIZE);

    // How many pixels each object actually covers — used to tell the user when a
    // region simply isn't in the photo (e.g. a fully bald head, or no face).
    const counts = { face: 0, skin: 0, hair: 0, shadow: 0 };
    const sets = {
        face: new Set(CLS_FACE),
        skin: new Set(CLS_SKIN),
        hair: new Set(CLS_HAIR),
        shadow: new Set(CLS_SHADOW),
    };
    for (let p = 0; p < labels.length; p++) {
        const l = labels[p];
        if (l === 0) continue;
        const beard = facialHair[p] === 1;
        for (const k of OBJECT_KINDS) {
            // Facial hair belongs to Hair, and is explicitly not part of the
            // skin-tone or shadow regions (see maskIncludes).
            if (k === 'hair' ? (sets.hair.has(l) || beard) : (sets[k].has(l) && !beard)) counts[k]++;
        }
    }
    const minPx = Math.round(place.sw * place.sh * 0.002); // 0.2% of the photo
    const found = {};
    for (const k of OBJECT_KINDS) found[k] = counts[k] > minPx;

    return {
        image, labels, facialHair, place,
        imgW: image.naturalWidth,
        imgH: image.naturalHeight,
        found,
        maskCache: new Map(),
    };
}

// Hair owns the beard/moustache; the skin-tone and shadow regions exclude it, so
// brightening skin or opening shadows never bleaches facial hair.
function maskIncludes(kind, label, ids, isBeard) {
    if (kind === 'hair') return ids.has(label) || isBeard;
    return ids.has(label) && !isBeard;
}

/* ------------------------------------------------------------------ *
 *  Hand-painted corrections
 *
 *  The parser is good but never exactly right — it drops a jawline into the
 *  background, or takes the frame of a pair of glasses for skin. So each object
 *  carries an optional edit layer in parser space, painted by the user:
 *  EDIT_ADD forces a pixel in, EDIT_REMOVE forces it out, 0 leaves the model's
 *  own answer alone. It is applied before the feather, so a brushed edge comes
 *  out as soft as a detected one.
 * ------------------------------------------------------------------ */

const EDIT_NONE = 0;
const EDIT_ADD = 1;
const EDIT_REMOVE = 2;

/** The edit layer for one object, allocated on first use. */
function getEditLayer(parse, kind, create = false) {
    if (!parse.edits) {
        if (!create) return null;
        parse.edits = {};
    }
    if (!parse.edits[kind] && create) {
        parse.edits[kind] = new Uint8Array(PARSE_SIZE * PARSE_SIZE);
    }
    return parse.edits[kind] || null;
}

/* ── Stroke history ──
 * One entry per brush stroke (pointer down → up), not per painted segment, so
 * Undo walks back the way the user thinks of their own work. Snapshots are the
 * whole 512² layer: 256KB each, which is why the stack is short.
 */
const MAX_EDIT_HISTORY = 15;

/** Snapshot the layer before a stroke begins. */
export function beginMaskEditStroke(parse, kind) {
    if (!parse) return;
    if (!parse.editHistory) parse.editHistory = {};
    const stack = parse.editHistory[kind] || (parse.editHistory[kind] = []);
    const layer = getEditLayer(parse, kind);
    // `null` records "there were no edits at all yet" — undoing back to that
    // has to remove the layer, not leave an empty one behind.
    stack.push(layer ? new Uint8Array(layer) : null);
    if (stack.length > MAX_EDIT_HISTORY) stack.shift();
}

/** Roll back the most recent stroke. @returns {boolean} whether anything moved */
export function undoMaskEdit(parse, kind) {
    const stack = parse?.editHistory?.[kind];
    if (!stack || !stack.length) return false;

    const snapshot = stack.pop();
    if (snapshot === null) {
        if (parse.edits) delete parse.edits[kind];
    } else {
        if (!parse.edits) parse.edits = {};
        parse.edits[kind] = snapshot;
    }
    invalidateMaskCache(parse, kind);
    return true;
}

export function canUndoMaskEdit(parse, kind) {
    return !!parse?.editHistory?.[kind]?.length;
}

export function hasMaskEdits(parse, kind) {
    const layer = parse && getEditLayer(parse, kind);
    if (!layer) return false;
    for (let p = 0; p < layer.length; p++) if (layer[p] !== EDIT_NONE) return true;
    return false;
}

/** Throw away the hand-painted corrections for one object. */
export function resetMaskEdits(parse, kind) {
    if (parse?.edits?.[kind]) {
        parse.edits[kind] = null;
        delete parse.edits[kind];
        invalidateMaskCache(parse, kind);
    }
}

/** Masks are cached per render size, so an edit has to drop every size. */
function invalidateMaskCache(parse, kind) {
    for (const key of [...parse.maskCache.keys()]) {
        if (key.startsWith(`${kind}:`)) parse.maskCache.delete(key);
    }
}

/**
 * Paint one brush segment into an object's edit layer.
 *
 * Coordinates are normalised to the photo (0-1) so the caller never has to know
 * about the 512² letterbox the parser works in. A segment rather than a point:
 * pointer events arrive far apart during a fast stroke, and stamping only at the
 * sampled positions leaves a dotted line.
 *
 * @param {'add'|'erase'} mode
 * @param {number} radius brush radius, as a fraction of the photo's long edge
 * @returns {boolean} whether anything actually changed
 */
export function paintMaskEdit(parse, kind, { fromX, fromY, toX, toY, radius, mode }) {
    if (!parse) return false;
    const layer = getEditLayer(parse, kind, true);
    const { dx, dy, sw, sh } = parse.place;
    const value = mode === 'erase' ? EDIT_REMOVE : EDIT_ADD;

    // Normalised photo space → parser pixels.
    const toPx = (u, v) => [dx + u * sw, dy + v * sh];
    const [x0, y0] = toPx(fromX, fromY);
    const [x1, y1] = toPx(toX, toY);
    const r = Math.max(1, radius * Math.max(sw, sh));

    // One stamp per half-radius keeps the stroke solid without redundant work.
    const dist = Math.hypot(x1 - x0, y1 - y0);
    const steps = Math.max(1, Math.ceil(dist / Math.max(1, r * 0.5)));
    const r2 = r * r;
    let touched = false;

    for (let s = 0; s <= steps; s++) {
        const cx = x0 + ((x1 - x0) * s) / steps;
        const cy = y0 + ((y1 - y0) * s) / steps;
        const minX = Math.max(0, Math.floor(cx - r));
        const maxX = Math.min(PARSE_SIZE - 1, Math.ceil(cx + r));
        const minY = Math.max(0, Math.floor(cy - r));
        const maxY = Math.min(PARSE_SIZE - 1, Math.ceil(cy + r));
        for (let py = minY; py <= maxY; py++) {
            const ddy = py - cy;
            for (let px = minX; px <= maxX; px++) {
                const ddx = px - cx;
                if (ddx * ddx + ddy * ddy > r2) continue;
                const idx = py * PARSE_SIZE + px;
                if (layer[idx] !== value) { layer[idx] = value; touched = true; }
            }
        }
    }

    if (touched) invalidateMaskCache(parse, kind);
    return touched;
}

// Binary mask in parser space (512²), cropped later to the letterboxed area.
function buildRawMaskCanvas(labels, facialHair, kind, edits) {
    const ids = new Set(KIND_CLASSES[kind]);
    const c = document.createElement('canvas');
    c.width = PARSE_SIZE; c.height = PARSE_SIZE;
    const ctx = c.getContext('2d');
    const img = ctx.createImageData(PARSE_SIZE, PARSE_SIZE);
    const d = img.data;
    for (let p = 0, i = 0; p < labels.length; p++, i += 4) {
        const edit = edits ? edits[p] : EDIT_NONE;
        const on = edit === EDIT_ADD ? 255
            : edit === EDIT_REMOVE ? 0
                : (maskIncludes(kind, labels[p], ids, facialHair[p] === 1) ? 255 : 0);
        d[i] = 255; d[i + 1] = 255; d[i + 2] = 255; d[i + 3] = on;
    }
    ctx.putImageData(img, 0, 0);
    return c;
}

/**
 * Feathered mask for one object, scaled to an arbitrary render size.
 * Returns a Uint8ClampedArray of `w*h` alpha values (0-255), cached per size.
 */
export function getMask(parse, kind, w, h) {
    const key = `${kind}:${w}x${h}`;
    const hit = parse.maskCache.get(key);
    if (hit) return hit;

    const raw = buildRawMaskCanvas(parse.labels, parse.facialHair, kind, getEditLayer(parse, kind));
    const { dx, dy, sw, sh } = parse.place;

    // Feather scales with the output size so the edge looks the same whether we
    // are drawing a small preview or the full-resolution export.
    const feather = Math.max(1.5, Math.min(14, Math.max(w, h) * 0.006));

    const out = document.createElement('canvas');
    out.width = w; out.height = h;
    const ctx = out.getContext('2d', { willReadFrequently: true });
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    try { ctx.filter = `blur(${feather.toFixed(2)}px)`; } catch { /* older browsers: hard edge */ }
    ctx.drawImage(raw, dx, dy, sw, sh, 0, 0, w, h);
    ctx.filter = 'none';

    const data = ctx.getImageData(0, 0, w, h).data;
    const mask = new Uint8ClampedArray(w * h);
    for (let p = 0, i = 3; p < mask.length; p++, i += 4) mask[p] = data[i];
    parse.maskCache.set(key, mask);
    return mask;
}

// The sliders travel the full 0-200 for a natural feel, but the ends of that
// range are unusable on a face (a black or blown-out mask). The displayed value
// is therefore remapped onto a gentler effective range before it reaches the
// curve — 100 stays exactly neutral, and the extremes land on TONE_MIN/TONE_MAX.
const TONE_MIN = 50;
const TONE_MAX = 130;

function toneScale(v) {
    if (v === 100) return 100;
    return v < 100
        ? TONE_MIN + (v / 100) * (100 - TONE_MIN)
        : 100 + ((v - 100) / 100) * (TONE_MAX - 100);
}

// Brightness uses the same gamma curve as the editor's global Brightness slider,
// contrast the same pivot-at-mid-grey formula as CSS contrast(); both are folded
// into one 256-entry lookup table.
function buildToneLut(brightnessInput, contrastInput) {
    const brightness = toneScale(brightnessInput);
    const gamma = brightness >= 100 ? 1 - ((brightness - 100) / 170) : 1 + ((100 - brightness) / 50);
    const k = toneScale(contrastInput) / 100;
    const lut = new Uint8Array(256);
    for (let i = 0; i < 256; i++) {
        let v = Math.pow(i / 255, gamma) * 255;
        v = (v - 127.5) * k + 127.5;
        lut[i] = v < 0 ? 0 : (v > 255 ? 255 : v);
    }
    return lut;
}

function applyToneMasked(data, mask, lut) {
    for (let p = 0, i = 0; p < mask.length; p++, i += 4) {
        const t = mask[p] / 255;
        if (t <= 0.002) continue;
        data[i] += (lut[data[i]] - data[i]) * t;
        data[i + 1] += (lut[data[i + 1]] - data[i + 1]) * t;
        data[i + 2] += (lut[data[i + 2]] - data[i + 2]) * t;
    }
}

// Opens up shadowed skin — the dark band under the chin/jaw from overhead or
// one-sided lighting. Strength is driven by how dark a pixel already is and
// falls to zero by SHADOW_KNEE, so lit skin is left completely alone (no flat,
// washed-out look). The additive term is what actually rescues near-black
// pixels; a pure multiply can never lift them.
const SHADOW_KNEE = 0.65;  // luminance above which nothing is touched
const SHADOW_LIFT = 0.25;  // additive strength at full amount
const SHADOW_GAIN = 0.5;   // multiplicative strength at full amount

function applyShadowMasked(data, mask, amount) {
    const a = amount / 100;
    for (let p = 0, i = 0; p < mask.length; p++, i += 4) {
        const t = (mask[p] / 255) * a;
        if (t <= 0.002) continue;
        const r = data[i], g = data[i + 1], b = data[i + 2];
        const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
        if (lum >= SHADOW_KNEE) continue;
        const w = Math.pow(1 - lum / SHADOW_KNEE, 1.6);
        const gain = 1 + SHADOW_GAIN * t * w;
        const lift = 255 * SHADOW_LIFT * t * w;
        const nr = r * gain + lift, ng = g * gain + lift, nb = b * gain + lift;
        data[i] = nr > 255 ? 255 : nr;
        data[i + 1] = ng > 255 ? 255 : ng;
        data[i + 2] = nb > 255 ? 255 : nb;
    }
}

// Neutral, hue-preserving hair darkening: already-dark pixels are pulled down
// less, so hair keeps its shape instead of turning into a flat black blob.
function applyDarkenMasked(data, mask, amount) {
    const a = amount / 100;
    for (let p = 0, i = 0; p < mask.length; p++, i += 4) {
        const t = (mask[p] / 255) * a;
        if (t <= 0.002) continue;
        const r = data[i], g = data[i + 1], b = data[i + 2];
        const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
        const weight = lum < 0.12 ? (lum / 0.12) : 1;
        const f = 1 - (0.55 * t * weight);
        data[i] = r * f;
        data[i + 1] = g * f;
        data[i + 2] = b * f;
    }
}

/**
 * Render the photo with every object adjustment applied, at `targetW×targetH`.
 * Use a small size for the live preview and the natural size for the export.
 *
 * @returns {HTMLCanvasElement}
 */
export function renderObjectAdjust(parse, settings, targetW, targetH) {
    const w = Math.max(1, Math.round(targetW));
    const h = Math.max(1, Math.round(targetH));
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(parse.image, 0, 0, w, h);

    if (!hasObjectChange(settings)) return canvas;

    const frame = ctx.getImageData(0, 0, w, h);
    const data = frame.data;

    // Shadow lift is a base correction — it runs before the tone sliders so
    // those operate on already-even skin rather than fighting the shadow.
    if (kindHasChange(settings, 'shadow')) {
        applyShadowMasked(data, getMask(parse, 'shadow', w, h), settings.shadow.lift);
    }
    // Skin first, then Face on top: Face is a subset of Skin, so a user who
    // brightens the whole skin and then tweaks the face gets the face value.
    for (const kind of ['skin', 'face']) {
        if (!kindHasChange(settings, kind)) continue;
        const { brightness, contrast } = settings[kind];
        applyToneMasked(data, getMask(parse, kind, w, h), buildToneLut(brightness, contrast));
    }
    if (kindHasChange(settings, 'hair')) {
        applyDarkenMasked(data, getMask(parse, 'hair', w, h), settings.hair.darken);
    }

    ctx.putImageData(frame, 0, 0);
    return canvas;
}

/** Full-resolution render, encoded as a PNG blob (keeps transparency). */
export function renderObjectAdjustBlob(parse, settings) {
    const canvas = renderObjectAdjust(parse, settings, parse.imgW, parse.imgH);
    return new Promise((resolve, reject) => {
        canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('encode failed'))), 'image/png');
    });
}

/** Preview canvas that paints the selected object's mask as a coloured overlay. */
export function renderMaskOverlay(parse, kind, targetW, targetH, rgb = [56, 189, 248]) {
    const w = Math.max(1, Math.round(targetW));
    const h = Math.max(1, Math.round(targetH));
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d');
    const img = ctx.createImageData(w, h);
    const d = img.data;
    const mask = getMask(parse, kind, w, h);
    for (let p = 0, i = 0; p < mask.length; p++, i += 4) {
        d[i] = rgb[0]; d[i + 1] = rgb[1]; d[i + 2] = rgb[2];
        d[i + 3] = Math.round(mask[p] * 0.5);
    }
    ctx.putImageData(img, 0, 0);
    return canvas;
}
